export type Role = 'administrador' | 'editor' | 'lider' | 'musico' | 'miembro';

export interface User {
  email: string;
  name: string;
  role: Role;
  /** Qué toca. Se muestra junto al nombre en la lista del servicio. */
  instrument?: string | null;
  active?: boolean;
  tenantId?: string;
  tenantName?: string;
  tenantSettings?: Record<string, any>;
  isSuperAdmin?: boolean;
  /** El grupo es de demostración: el backend rechaza toda escritura. */
  isDemo?: boolean;
  createdAt?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

/**
 * Una categoría del grupo. Dejaron de ser una lista fija del sistema: cada
 * grupo arma las suyas, las renombra y las ordena.
 */
export interface Category {
  id: string;
  name: string;
  position: number;
  /** Cuántas canciones la usan. Es lo que decide si se puede borrar. */
  songCount: number;
}

/** @deprecated Las categorías ya no son fijas; quedan como semilla histórica. */
export type SongCategory =
  | 'himno' | 'alabanza' | 'coro' | 'adoracion'
  | 'propia' | 'cover' | 'instrumental' | 'otro';
export type SectionType = 'estrofa' | 'coro' | 'precoro' | 'puente' | 'intro' | 'final';

export interface SongSection {
  id?: string;
  sectionType: SectionType;
  label?: string | null;
  position: number;
  /** Líneas alternas: impar = acordes, par = letra. */
  content: string;
}

export interface SongVideo {
  id?: string;
  url: string;
  label?: string | null;
  position: number;
}

export interface Song {
  id: string;
  tenantId?: string;
  title: string;
  author?: string | null;
  songNumber?: number | null;
  songKey?: string | null;
  tempo?: number | null;
  /** Nombre de la categoría, tal como lo puso el grupo. */
  category: string | null;
  categoryId?: string | null;
  tags: string[];
  notes?: string | null;
  hasChords: boolean;
  isPublic: boolean;
  /** Visible en el cancionero público (internet), distinto de `isPublic`. */
  isShared?: boolean;
  createdBy?: string | null;
  /** Última vez que se tocó, de las listas pasadas. AAAA-MM-DD. */
  lastPlayed?: string | null;
  createdAt?: string;
  updatedAt?: string;
  sections?: SongSection[];
  videos?: SongVideo[];
  sectionCount?: number;
}

/** Payload de creación/edición (el backend reemplaza las secciones). */
export interface SongInput {
  title: string;
  author?: string | null;
  songNumber?: number | null;
  songKey?: string | null;
  tempo?: number | null;
  categoryId: string | null;
  tags: string[];
  notes?: string | null;
  isPublic: boolean;
  isShared: boolean;
  sections: SongSection[];
  videos: { url: string; label?: string | null; position: number }[];
}

/** Una canción dentro de una lista del servicio. */
export interface SetlistItem {
  id?: string;
  songId: string;
  position: number;
  /** Tono de ESA vez; null = el tono guardado en la canción. */
  songKey?: string | null;
  /** Nombre del cantante. Si es alguien del grupo, el backend pone el suyo. */
  singer?: string | null;
  /** Cuenta del grupo, cuando el cantante la tiene. */
  singerEmail?: string | null;
  singerInstrument?: string | null;
  notes?: string | null;
  // Datos de la canción, que el backend trae ya resueltos
  title?: string;
  songNumber?: number | null;
  originalKey?: string | null;
  category?: string | null;
  hasChords?: boolean;
  /** Esta canción tiene acordes arreglados solo para esta lista. */
  hasOwnVersion?: boolean;
}

/** Quién ha cantado una canción y en qué tono. Se deduce del historial. */
export interface CantanteDeCancion {
  singerEmail: string | null;
  singer: string;
  instrument: string | null;
  songKey: string | null;
  lastDate: string | null;
  times: number;
}

export interface Setlist {
  id: string;
  name: string;
  serviceDate?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  /** Última vez que se tocó, de las listas pasadas. AAAA-MM-DD. */
  lastPlayed?: string | null;
  createdAt?: string;
  updatedAt?: string;
  items?: SetlistItem[];
  songCount?: number;
}

export interface SetlistInput {
  name: string;
  serviceDate?: string | null;
  notes?: string | null;
  items: {
    songId: string;
    position: number;
    songKey?: string | null;
    singer?: string | null;
    notes?: string | null;
  }[];
}

export interface Tenant {
  id: string;
  name: string;
  active: boolean;
  settings?: Record<string, any>;
  createdAt?: string;
  userCount?: number;
  songCount?: number;
  /** Grupo de demostración: se ve entero, pero no guarda nada. */
  isDemo?: boolean;
  /** Tiene el cancionero público encendido. */
  shareEnabled?: boolean;
  /** Cupos que fija el super-admin. `null` = sin límite. */
  maxUsers?: number | null;
  maxSongs?: number | null;
  /** Usuarios activos: es lo que cuenta contra `maxUsers`, no el total. */
  activeUserCount?: number;
}

export const CATEGORY_LABELS: Record<SongCategory, string> = {
  himno: 'Himno',
  alabanza: 'Alabanza',
  adoracion: 'Adoración',
  coro: 'Coro',
  propia: 'Propia',
  cover: 'Cover',
  instrumental: 'Instrumental',
  otro: 'Otro'
};

/** Etiqueta de una categoría, tolerante a valores que esta versión no conozca. */
export const categoryLabel = (category: string): string =>
  CATEGORY_LABELS[category as SongCategory] || category;

export const SECTION_LABELS: Record<SectionType, string> = {
  estrofa: 'Estrofa',
  coro: 'Coro',
  precoro: 'Pre-coro',
  puente: 'Puente',
  intro: 'Intro',
  final: 'Final'
};

export const ROLE_LABELS: Record<Role, string> = {
  administrador: 'Administrador',
  editor: 'Editor',
  lider: 'Líder',
  musico: 'Músico',
  miembro: 'Miembro'
};

/** Qué puede hacer cada rol, para explicarlo donde se elige. */
export const ROLE_HELP: Record<Role, string> = {
  administrador: 'Todo, incluidos usuarios y el enlace público',
  editor: 'Cancionero y listas del servicio',
  lider: 'Como el músico, y además dirige el en vivo de la app',
  musico: 'Listas del servicio; ve el cancionero pero no lo edita',
  miembro: 'Solo consultar'
};
